import React from 'react'
import { Box, Link, useMediaQuery, Divider } from '@mui/material'
import { styled } from '@mui/material/styles'
import Actions from './actions'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { Colors } from '../../styles/theme' // ADDED: Import Colors

// Styled components matching style_gen.css

const StyledHeader = styled(Box)(({ theme }) => ({
    width: 'calc(100% - 50px)',
    margin: '0 auto',
    background: Colors.primary, // CHANGED: was '#ffe9cf'
    marginBottom: '0.8rem',
}))

const LogoImage = styled('img')(({ theme }) => ({
    maxWidth: '200px',
    height: 'auto',
    borderTop: '1px solid rgb(220, 219, 219)',
    borderRight: `1px solid ${Colors.secondary}`, // CHANGED: was '#005247'
    borderBottom: `1px solid ${Colors.secondary}`, // CHANGED: was '#005247'
    borderLeft: '1px solid rgb(220, 219, 219)',
    borderRadius: '4px',
    boxShadow: '0 8px 10px rgba(0, 0, 0, 0.2)',
    marginRight: '24px',
    marginTop: '0.75rem',
}))

const StyledNav = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    width: '100%',
    padding: 0,
    backgroundColor: 'transparent',
    borderBottom: 'none',
    fontFamily: '"Roboto", Arial, sans-serif',
}))

const NavLeft = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    marginLeft: '20px',
    marginRight: '20px',

    '@media (max-width: 1075px)': {
        marginLeft: '8px',
        marginRight: '8px',
    },
}))

const StyledNavLink = styled(Link)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    height: '48px',
    textDecoration: 'none',
    borderRadius: '4px',
    border: `2px solid ${Colors.boltGold}`,
    padding: '12px 24px', // CHANGED: 10px → 12px (maintains 48px height)
    backgroundColor: Colors.secondary, // CHANGED: Solid background
    color: Colors.white, // CHANGED: White text
    fontWeight: 600, // CHANGED: Bolder
    fontSize: '1rem', // CHANGED: Standard size
    textAlign: 'center',
    // border: 'none', // CHANGED: No border
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0, 82, 71, 0.1)', // ADDED: Subtle shadow
    transition: 'all 0.2s ease', // CHANGED: Smoother transition
    // ADDED: Disable tap highlight (iOS)
    WebkitTapHighlightColor: 'transparent',

    // ADDED: Responsive font sizing below 1075px
    '@media (max-width: 1075px)': {
        fontSize: '0.85rem', // Smaller font
        padding: '10px 10px', // Tighter padding
    },

    // Hover for desktop
    '&:hover': {
        backgroundColor: Colors.greenLight,
        color: Colors.white,
        boxShadow: '0 4px 12px rgba(0, 82, 71, 0.2)',
        transform: 'translateY(-1px)',
    },

    // ADDED: Active state for touch devices
    '&:active': {
        backgroundColor: Colors.greenDark, // Darker on tap
        transform: 'scale(0.98)', // Slight press effect
        boxShadow: '0 2px 4px rgba(0, 82, 71, 0.1)',
    },

    // ADDED: Focus state for accessibility
    '&:focus-visible': {
        outline: `3px solid ${Colors.greenLight}`,
        outlineOffset: '2px',
    },
}))

export default function AppbarDesktop({ matches }) {
    const theme = useTheme()
    const location = useLocation()
    const matches2 = useMediaQuery(theme.breakpoints.down('lg'))
    const estLabel = matches2 ? 'Estimator' : 'Materials Estimator'
    const manLabel = matches2 ? 'Manual' : 'Install. Manual'

    // Check if we're on the products page (home/root)
    const isProductsPage =
        location.pathname === '/' || location.pathname === '/shop'

    return (
        <StyledHeader>
            <StyledNav component="nav">
                <LogoImage
                    src="/shop/images/appbar/logo.png"
                    alt="ElectroBraid Logo"
                />

                <NavLeft>
                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: '0rem',
                            marginRight: '1rem',
                        }}
                        flexItem
                    />

                    <StyledNavLink
                        href="/"
                        underline="none"
                        aria-label="Visit front page"
                    >
                        Home
                    </StyledNavLink>

                    <StyledNavLink
                        href="/calc"
                        underline="none"
                        aria-label="Use the Fence Materials Estimator App"
                    >
                        {estLabel}
                    </StyledNavLink>

                    {/* Show Products link only when NOT on products page */}
                    {!isProductsPage && (
                        <StyledNavLink
                            component={RouterLink}
                            to="/"
                            underline="none"
                            aria-label="Visit Products section"
                        >
                            Products
                        </StyledNavLink>
                    )}

                    {/* Show Install Manual link only when ON products page */}
                    {isProductsPage && (
                        <StyledNavLink
                            href="/manual"
                            underline="none"
                            aria-label="Visit Install Manual"
                        >
                            {manLabel}
                        </StyledNavLink>
                    )}

                    <Divider
                        orientation="vertical"
                        sx={{
                            marginLeft: '1rem',
                            marginRight: '0rem',
                        }}
                        flexItem
                    />
                </NavLeft>

                <Actions matches={matches} />
            </StyledNav>
        </StyledHeader>
    )
}
